import Raven from 'raven-js';
// @ts-expect-error build-time virtual module
import { bender } from 'legacy-hubspot-bender-context';
import PortalIdParser from 'PortalIdParser';
import { getI18nProvider } from 'quartz-i18n/service';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Route, Switch } from 'quartz-routing/react-router';
import { useHistory } from 'quartz-routing';
import { fetchGraniteRemote, loadRemoteDlbs, loadRemoteModule, registerRemoteModules } from '../internal/module-federation/runtime';
import { activateRemoteCss } from './remoteCss';
import { bindLinkRewriter } from './linkRewriter';
import { setCopilotLayoutType } from 'quartz/framework/copilot/service';
import { updateGridClasses } from '../internal/grid';
import { getGraniteHostOptions } from '../internal/module-federation/utils';
import { createAttachHistory, usePopstateCapture } from './graniteHistory';
import { graniteRouteComparator } from './graniteRouteUtils';
import { ErrorPage } from '../components/ErrorPage';
import { getAuthResult } from 'quartz-auth/service';
import { setup } from '../internal/module-federation/init';
import { reconfigure } from 'raven-hubspot/configure';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// strip the unique portalId out of pathname so it doesn't cause unnecessary uniqueness
function metricsSafePathname() {
  const pathname = window.location.pathname;
  const portalId = PortalIdParser.get();
  if (portalId) {
    const endOfStringMatcher = new RegExp(`/${portalId}$`);
    return pathname.replace(`/${portalId}/`, `/:portalId/`).replace(endOfStringMatcher, '/:portalId');
  }
  return pathname;
}
let activeAppName = null;
let activeRemotePathname = metricsSafePathname();
export function createOrUpdateRouteDefinition(routeDefMap, remoteModule, path, priority, createComponent) {
  if (!routeDefMap[remoteModule]) {
    routeDefMap[remoteModule] = {
      path: [path],
      priorities: [priority],
      Component: createComponent()
    };
  } else {
    routeDefMap[remoteModule].path.push(path);
    routeDefMap[remoteModule].priorities.push(priority);
  }
}

/**
 * This should only be called within the render function of a granite route
 */
export function useSoftNavigationContext(appName) {
  const [softNavigationContext] = useState(() => {
    let context;
    if (activeAppName && activeAppName !== appName) {
      context = {
        timestamp: performance.now(),
        fromPathname: activeRemotePathname,
        fromRoute: activeRemotePathname,
        fromRemoteApp: activeAppName
      };
    }
    activeAppName = appName;
    activeRemotePathname = metricsSafePathname();
    return context;
  });
  return softNavigationContext;
}
export function createWrappedRemoteApp(remoteModule) {
  function initRemoteApp() {
    // Reconfigure before async loading so errors during DLB/module fetch
    // are attributed to this remote rather than the host. render() will
    // reconfigure again with full version info once the module is loaded.
    reconfigure({
      project: remoteModule
    });
    return {
      translationsPromise: loadRemoteModule(remoteModule, 'lang').then(mod => mod.LANG),
      remoteAppPromise: Promise.all([loadRemoteDlbs(remoteModule), fetchGraniteRemote(remoteModule)]).then(([, lazyModule]) => lazyModule.loadEntry()),
      cssPromise: activateRemoteCss(remoteModule)
    };
  }
  function RemoteApp() {
    const [error, setError] = useState();
    const softNavigationContext = useSoftNavigationContext(remoteModule);
    const rootRef = useRef(undefined);
    const containerCallback = useCallback(container => {
      if (!container) return;
      const {
        remoteAppPromise,
        translationsPromise,
        cssPromise
      } = initRemoteApp();
      Promise.all([remoteAppPromise, translationsPromise, cssPromise]).then(([remoteAppModule, translations]) => {
        if (!remoteAppModule) {
          throw new Error('Failed to load granite remote app module. Module undefined.');
        }
        const {
          copilotLayoutType
        } = remoteAppModule;
        if (copilotLayoutType !== undefined) {
          setCopilotLayoutType(copilotLayoutType);
          updateGridClasses(copilotLayoutType);
        }
        return remoteAppModule.render({
          softNavigationContext,
          container,
          authResult: getAuthResult(),
          translations: translations,
          localeInfo: getI18nProvider().localeInfo,
          attachHistory: createAttachHistory(remoteModule)
        }).then(renderedRoot => {
          rootRef.current = renderedRoot;
        });
      }).catch(e => {
        Raven.captureException(e, {
          tags: {
            project: remoteModule
          },
          extra: {
            hostProject: bender.currentProject,
            hostRelease: bender.currentProjectVersion
          }
        });
        setError(e);
      });
    },
    // IMPORTANT: deps must stay []. Adding deps changes the callback
    // reference, causing React to remount the app via ref detach/reattach
    // without the cleanup effect running between mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    useEffect(() => {
      return () => {
        // setTimeout avoids unmounting during a render cycle
        setTimeout(() => {
          var _rootRef$current;
          (_rootRef$current = rootRef.current) === null || _rootRef$current === void 0 || _rootRef$current.unmount();
          rootRef.current = undefined;
        }, 0);
      };
    }, []);
    if (error) {
      return /*#__PURE__*/_jsx(ErrorPage, {
        error: error
      });
    }
    return /*#__PURE__*/_jsx("div", {
      ref: containerCallback
    });
  }
  RemoteApp.displayName = `RemoteApp(${remoteModule})`;
  return RemoteApp;
}
function getRouteDefinitions() {
  const graniteOptions = getGraniteHostOptions();
  const routeDefinitions = {};
  const localRouteOverrides = {};
  graniteOptions.remoteRoutes.forEach(route => {
    var _route$priority, _route$localRedirects;
    createOrUpdateRouteDefinition(routeDefinitions, route.remoteModule, route.path.replace(':portalId', `${PortalIdParser.get()}`).replace(/\/(\{\*\}\?|\*\?|\*)$/, ''), (_route$priority = route.priority) !== null && _route$priority !== void 0 ? _route$priority : 0, () => createWrappedRemoteApp(route.remoteModule));
    (_route$localRedirects = route.localRedirects) === null || _route$localRedirects === void 0 || _route$localRedirects.forEach(pathToRedirect => {
      localRouteOverrides[pathToRedirect] = {
        path: route.path,
        remoteModule: route.remoteModule
      };
    });
    localRouteOverrides[route.path] = {
      path: route.path,
      remoteModule: route.remoteModule
    };
  });
  return {
    routeDefinitions,
    localRouteOverrides
  };
}
const GraniteRouterBase = ({
  getRoutes,
  children
}) => {
  const {
    routeDefinitions,
    localRouteOverrides
  } = useMemo(() => getRoutes(), [getRoutes]);
  const history = useHistory();
  useEffect(() => {
    const unbind = bindLinkRewriter(history, localRouteOverrides);
    return () => {
      unbind();
    };
  }, [history, localRouteOverrides]);
  usePopstateCapture(localRouteOverrides);
  useEffect(() => {
    setup();
    registerRemoteModules(Object.keys(routeDefinitions));
  }, [routeDefinitions]);

  // Flatten to one entry per path and sort via graniteRouteComparator so
  // <Switch> matches routes in the same order the CDN would on a hard nav.
  const sortedRoutes = useMemo(() => Object.values(routeDefinitions).flatMap(({
    path,
    priorities,
    Component
  }) => path.map((p, i) => ({
    path: p,
    priority: priorities[i],
    Component
  }))).sort(graniteRouteComparator), [routeDefinitions]);
  return /*#__PURE__*/_jsxs(Switch, {
    children: [sortedRoutes.map(({
      path,
      Component
    }, i) => /*#__PURE__*/_jsx(Route, {
      path: path,
      component: Component
    }, `granite-route-${i}`)), children]
  });
};
export const GraniteRouter = /*#__PURE__*/memo(GraniteRouterBase);
export function GraniteRoutes() {
  const getRoutes = useCallback(() => getRouteDefinitions(), []);
  return /*#__PURE__*/_jsx(GraniteRouter, {
    getRoutes: getRoutes
  });
}